import { Layout } from '@/components/Layout'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthGuard } from '@/components/AuthGuard'
import { Toaster } from '@/components/ui/toaster'
import { useAuthStore } from '@/lib/store'
import { Providers } from './components/providers'
import { RoutePath } from './utils/routes'
import { Suspense } from 'react'
import GlobalLoader from './components/GlobalLoader'
import loadable from '@loadable/component'
import { GlobalError } from '@/components/GlobalError'
import { GianoWatcher } from './components/GianoWatcher'
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react'

// Lazy imports for route components
const Login = loadable(() => import('@/pages/login'))
const Unauthorized = loadable(() => import('@/pages/unauthorized'))
const Users = loadable(() => import('@/pages/users'))
const Organizations = loadable(() => import('@/pages/organizations'))
const Agreements = loadable(() => import('@/pages/admin/agreements'))
const Agreement = loadable(() => import('@/pages/admin/agreement'))
const Disputes = loadable(() => import('@/pages/admin/disputes'))
const Dispute = loadable(() => import('@/pages/admin/dispute'))
const AdminTransactions = loadable(() => import('@/pages/admin/transactions'))
const AdminUsers = loadable(() => import('@/pages/admin/users'))
const Milestone = loadable(() => import('@/pages/admin/milestone'))
const NotFound = loadable(() => import('@/pages/not-found'))
const RegisterTenantPage = loadable(() => import('./pages/register/tenant'))
const LoginRootUser = loadable(() => import('./pages/login-root-user'))
const RootAdminsSignup = loadable(() => import('./pages/root-admins/signup'))
const RegisterRootAdmin = loadable(() => import('./pages/register/root-admin'))
const Organization = loadable(() => import('./pages/organization'))
const ApiKey = loadable(() => import('./pages/admin/api-key'))
const SharedBridgeWebhook = loadable(() => import('./pages/root-admins/shared-bridge-webhook'))
const ProviderConfigs = loadable(() => import('./pages/admin/provider-configs'))
const PaymentProviders = loadable(() => import('./pages/admin/payment-providers'))

function App() {
  const user = useAuthStore((state) => state.user)

  return (
    <SentryErrorBoundary fallback={GlobalError}>
      <Providers>
        <Suspense fallback={<GlobalLoader />}>
          <Routes>
            <Route path={RoutePath.RegisterTenant} element={<RegisterTenantPage />} />
            <Route path={RoutePath.RegisterRootAdmin} element={<RegisterRootAdmin />} />
            <Route path={RoutePath.Login} element={<Login />} />
            <Route path={RoutePath.RootAdminLogin} element={<LoginRootUser />} />
            <Route path={RoutePath.Unauthorized} element={<Unauthorized />} />
            <Route path={RoutePath.RootAdminSignup} element={<RootAdminsSignup />} />
            <Route
              element={
                <AuthGuard>
                  <GianoWatcher>
                    <Layout />
                  </GianoWatcher>
                </AuthGuard>
              }
            >
              <Route
                path={RoutePath.Root}
                element={
                  <Navigate
                    to={user?.role === 'root_admin' ? RoutePath.Organizations : RoutePath.AdminAgreements}
                    replace
                  />
                }
              />

              {/* Root Admin Routes */}
              <Route
                path={RoutePath.Organizations}
                element={
                  <AuthGuard allowedRoles={['root_admin']}>
                    <Organizations />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.Users}
                element={
                  <AuthGuard allowedRoles={['root_admin']}>
                    <Users />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.Organization}
                element={
                  <AuthGuard allowedRoles={['root_admin']}>
                    <Organization />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.OrganizationProviders}
                element={
                  <AuthGuard allowedRoles={['root_admin']}>
                    <ProviderConfigs />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.SharedBridgeWebhook}
                element={
                  <AuthGuard allowedRoles={['root_admin']}>
                    <SharedBridgeWebhook />
                  </AuthGuard>
                }
              />

              {/* Admin Routes */}
              <Route
                path={RoutePath.AdminAgreements}
                element={
                  <AuthGuard allowedRoles={['admin']}>
                    <Agreements />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.AdminAgreement}
                element={
                  <AuthGuard allowedRoles={['admin']}>
                    <Agreement />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.AdminDisputes}
                element={
                  <AuthGuard allowedRoles={['admin']}>
                    <Disputes />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.AdminDispute}
                element={
                  <AuthGuard allowedRoles={['admin']}>
                    <Dispute />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.AdminTransactions}
                element={
                  <AuthGuard allowedRoles={['admin']}>
                    <AdminTransactions />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.AdminUsers}
                element={
                  <AuthGuard allowedRoles={['admin']}>
                    <AdminUsers />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.AdminMilestone}
                element={
                  <AuthGuard allowedRoles={['admin']}>
                    <Milestone />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.AdminApiKey}
                element={
                  <AuthGuard allowedRoles={['admin']}>
                    <ApiKey />
                  </AuthGuard>
                }
              />
              <Route
                path={RoutePath.AdminPaymentProviders}
                element={
                  <AuthGuard allowedRoles={['admin']}>
                    <PaymentProviders />
                  </AuthGuard>
                }
              />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Toaster />
      </Providers>
    </SentryErrorBoundary>
  )
}

export default App
